import { Ref, Text } from "@fluentui/react-northstar";
import { TeamsUser } from "@teams/types";
import { QuestionnaireTemplateDTO } from "@teams/types/api";
import React, { useState } from "react";

import { ConfirmActionPopup } from "../../../../_shared/components/ConfirmActionPopup";
import { OverflowMenu } from "../../../../_shared/components/Menus/OverflowMenu/OverflowMenu";
import { PollyDialog } from "../../../../_shared/components/PollyDialog";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { UsersAndPermissions } from "../../../../_shared/components/UsersAndPermissions";
import { isError } from "../../../../_shared/services/ChannelApi";
import { templatesApi } from "../../../../_shared/services/TemplatesApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";

type Props = {
  template: QuestionnaireTemplateDTO;
  onDeleted: (template: QuestionnaireTemplateDTO) => void;
};

export const TemplateManagementMenu: React.FC<Props> = ({ template, onDeleted }) => {
  const [showUsersDialog, setShowUsersDialog] = useState<boolean>();
  const [showDeleteConfim, setShowDeleteConfim] = useState<boolean>();
  const [deleteRef, setDeleteRef] = useState<React.ReactNode>(null);
  const [owners, setOwners] = useState(template.owners || []);
  const [users, setUsers] = useState(template.users || []);
  const { teamsContext } = useTeamsContext();

  if (template.role !== "own") {
    return null;
  }

  return (
    <>
      <Ref innerRef={setDeleteRef}>
        <OverflowMenu
          items={[
            {
              key: "users",
              showInOverflow: true,
              icon: <PollyIcon icon="share-icon" inheritColor />,
              content: "Share",
              onClick: e => {
                e.stopPropagation();
                setShowUsersDialog(true);
              },
            },
            {
              key: "delete",
              showInOverflow: true,
              icon: <PollyIcon icon="trash-icon" inheritColor />,
              content: "Delete",
              onClick: e => {
                e.stopPropagation();
                setShowDeleteConfim(true);
              },
            },
          ]}
          maxExposedItemsCount={2}
        />
      </Ref>

      <ConfirmActionPopup
        open={showDeleteConfim}
        target={deleteRef}
        content={
          <>
            Are you sure you want to delete <Text weight="bold">{template.templateName}</Text>{" "}
            template?
          </>
        }
        onCancel={() => setShowDeleteConfim(false)}
        onConfirmedAction={async () => {
          try {
            await templatesApi.deleteTemplate(teamsContext, template.id);
            onDeleted(template);
          } catch (e) {
            return e as Error;
          }
        }}
      />

      {showUsersDialog && (
        <PollyDialog
          open
          onCancel={() => {
            setShowUsersDialog(false);
          }}
          header={
            <div onClick={e => e.stopPropagation()}>
              {`Share Template: ${template.templateName}`}
            </div>
          }
        >
          <div className="series-menu__users-dialog" onClick={e => e.stopPropagation()}>
            <UsersAndPermissions
              ownersHeader="Template management"
              ownersHeaderDescription="Users that have management access to this template"
              usersHeader="Read only access"
              usersHeaderDescription="Users that are able to use this template or copy it"
              canOwn={template.role === "own"}
              owners={owners}
              users={users}
              showUsers
              onAddOwner={async owner => {
                if (template.role !== "own") {
                  return;
                }

                const newOwner: TeamsUser = {
                  aadObjectId: owner.aadObjectId,
                  name: owner.name,
                  teamsUserId: owner.id,
                  upn: owner.userPrincipalName,
                };

                const result = await templatesApi.addOwner(teamsContext, template.id, newOwner);
                if (!isError(result)) {
                  setOwners([...owners, newOwner]);
                }
                return result;
              }}
              onRemoveOwner={async user => {
                if (template.role !== "own") {
                  return;
                }

                const result = await templatesApi.removeOwner(
                  teamsContext,
                  template.id,
                  user.aadObjectId
                );
                if (!isError(result)) {
                  setOwners(owners.filter((u: TeamsUser) => u.aadObjectId !== user.aadObjectId));
                }
                return result;
              }}
              onAddUser={async user => {
                if (template.role !== "own") {
                  return;
                }

                const newUser: TeamsUser = {
                  aadObjectId: user.aadObjectId,
                  name: user.name,
                  teamsUserId: user.id,
                  upn: user.userPrincipalName,
                };

                const result = await templatesApi.addUser(teamsContext, template.id, newUser);
                if (!isError(result)) {
                  setUsers([...users, newUser]);
                }
                return result;
              }}
              onRemoveUser={async user => {
                if (template.role !== "own") {
                  return;
                }

                const result = await templatesApi.removeUser(
                  teamsContext,
                  template.id,
                  user.aadObjectId
                );
                if (!isError(result)) {
                  setUsers(users.filter((u: TeamsUser) => u.aadObjectId !== user.aadObjectId));
                }
                return result;
              }}
            />
          </div>
        </PollyDialog>
      )}
    </>
  );
};
